import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Home, Search, PlusCircle, LayoutDashboard, Shield, Building2, User, LogOut, Globe, Menu, X, Heart, Megaphone, FileText, BadgeCheck, MessageCircle, Video, Wallet, Scale } from 'lucide-react';
import BetaBadge from '@/components/BetaBadge';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';
import { useState } from 'react';

export default function Layout({ children }) {
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path;

  const isSeeker = isAuthenticated && user?.role === 'user';
  const navLinks = [
    { to: '/', label: t('nav.home'), icon: Home },
    { to: '/search', label: t('nav.search'), icon: Search },
    { to: '/submit', label: t('nav.submit'), icon: PlusCircle, auth: true, hideForSeeker: true },
    { to: '/become-agent', label: language === 'es' ? 'Convertirme en Agente' : 'Become an Agent', icon: BadgeCheck, seekerOnly: true },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background font-body">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B192C]/90 border-b border-white/10" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
              <span className="font-heading font-bold text-xl"><span className="text-[#D4AF37]">Waka</span><span className="text-white">Casa</span></span>
              <BetaBadge />
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(link => {
                if (link.auth && !isAuthenticated) return null;
                if (link.hideForSeeker && isSeeker) return null;
                if (link.seekerOnly && !isSeeker) return null;
                return (
                  <Link key={link.to} to={link.to} data-testid={`nav-${link.to.slice(1) || 'home'}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(link.to) ? 'bg-white/10 text-[#D4AF37]' : 'text-white/70 hover:text-white hover:bg-white/5'}`}>
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <button onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-white/20 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="language-toggle">
                <Globe className="w-3.5 h-3.5" />
                {language === 'en' ? 'ES' : 'EN'}
              </button>

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2 text-white hover:bg-white/10" data-testid="user-menu-trigger">
                      <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-[#D4AF37]" />
                      </div>
                      <span className="hidden sm:inline text-sm text-white/90">{user?.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {/* Role-specific dashboards at top */}
                    {user?.role === 'agent' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/agency')} data-testid="menu-agency">
                          <Building2 className="w-4 h-4 mr-2" />{language === 'es' ? 'Panel Agente' : 'Agent Dashboard'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/agent/tours')} data-testid="menu-tours">
                          <Video className="w-4 h-4 mr-2" />{language === 'es' ? 'Tours Virtuales' : 'Virtual Tours'}
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.role === 'lawyer' && (
                      <DropdownMenuItem onClick={() => navigate('/lawyer/dashboard')} data-testid="menu-lawyer-dashboard">
                        <Scale className="w-4 h-4 mr-2" />{language === 'es' ? 'Panel Abogado' : 'Lawyer Dashboard'}
                      </DropdownMenuItem>
                    )}
                    {(user?.role === 'admin' || user?.role === 'superadmin') && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <Shield className="w-4 h-4 mr-2" />{language === 'es' ? 'Panel Admin' : 'Admin Dashboard'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/payments')} data-testid="menu-admin-payments">
                          <Wallet className="w-4 h-4 mr-2" />{language === 'es' ? 'Cola de Pagos' : 'Payment Queue'}
                        </DropdownMenuItem>
                      </>
                    )}
                    {user?.role === 'superadmin' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/superadmin')} data-testid="menu-superadmin">
                          <Megaphone className="w-4 h-4 mr-2" />{language === 'es' ? 'Publicidad' : 'Ads & Featured'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/chatbot')} data-testid="menu-chatbot">
                          <MessageCircle className="w-4 h-4 mr-2" />{language === 'es' ? 'Chatbot WhatsApp' : 'WhatsApp Bot'}
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* User Dashboard for all */}
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />{['agent', 'admin', 'superadmin'].includes(user?.role) ? (language === 'es' ? 'Panel Usuario' : 'User Dashboard') : t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-favorites">
                      <Heart className="w-4 h-4 mr-2" />{t('nav.favorites')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/payments')} data-testid="menu-my-payments">
                      <Wallet className="w-4 h-4 mr-2" />{language === 'es' ? 'Mis Pagos' : 'My Payments'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/transactions')} data-testid="menu-transactions">
                      <FileText className="w-4 h-4 mr-2" />{language === 'es' ? 'Transacciones' : 'Transactions'}
                    </DropdownMenuItem>
                    {(user?.role === 'agent' || user?.role === 'lawyer') && (
                      <DropdownMenuItem onClick={() => navigate('/verification')} data-testid="menu-verification">
                        <BadgeCheck className="w-4 h-4 mr-2" />{language === 'es' ? 'Verificacion' : 'Verification'}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" />{language === 'es' ? 'Mi Perfil' : 'My Profile'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />{t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="nav-login">{t('nav.login')}</Button>
                  <Button size="sm" onClick={() => navigate('/auth?mode=register')} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0B192C] rounded-full px-5 font-semibold" data-testid="nav-register">{t('nav.register')}</Button>
                </div>
              )}

              {/* Mobile menu button */}
              <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#0B192C] px-4 py-3 space-y-1">
            {navLinks.map(link => {
              if (link.auth && !isAuthenticated) return null;
              if (link.hideForSeeker && isSeeker) return null;
              if (link.seekerOnly && !isSeeker) return null;
              return (
                <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive(link.to) ? 'bg-white/10 text-[#D4AF37]' : 'text-white/70'}`}>
                  <link.icon className="w-4 h-4" />{link.label}
                </Link>
              );
            })}
            {!isAuthenticated && (
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}>{t('nav.login')}</Button>
                <Button size="sm" className="flex-1 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" onClick={() => { navigate('/auth?mode=register'); setMobileMenuOpen(false); }}>{t('nav.register')}</Button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0B192C] text-white mt-auto" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="font-heading font-bold text-xl"><span className="text-[#D4AF37]">Waka</span><span className="text-white">Casa</span></span>
              </div>
              <p className="text-white/50 text-sm leading-relaxed">{t('footer.aboutText')}</p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-[#D4AF37] uppercase tracking-widest text-xs">{t('footer.quickLinks')}</h4>
              <div className="space-y-2.5">
                <Link to="/search" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors">{t('nav.search')}</Link>
                <Link to="/submit" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors">{t('nav.submit')}</Link>
                <Link to="/auth" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors">{t('nav.login')}</Link>
                <Link to="/privacy" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy-link">
                  {language === 'es' ? 'Privacidad' : 'Privacy'}
                </Link>
                <Link to="/terms" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-terms-link">
                  {language === 'es' ? 'Términos' : 'Terms'}
                </Link>
                <Link to="/data-deletion" className="block text-sm text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-data-deletion-link">
                  {language === 'es' ? 'Eliminar datos' : 'Delete data'}
                </Link>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-[#D4AF37] uppercase tracking-widest text-xs">{t('footer.contact')}</h4>
              <div className="space-y-2.5 text-sm text-white/60">
                <p>info@wakacasa.com</p>
                <p>+58 212 1234 567</p>
                <p>Av. Libertador, Chacao, Caracas</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center text-sm text-white/30">
            &copy; {new Date().getFullYear()} WakaCasa. {t('footer.rights')}
            <PlatformDisclaimer className="mt-3 text-white/30 max-w-3xl mx-auto" />
          </div>
        </div>
      </footer>
    </div>
  );
}
